import {
  AddConversationParticipantsDto,
  ConversationAttachmentPresignResponse,
  ConversationCandidateResponse,
  ConversationGroupListItemResponse,
  ConversationListItemResponse,
  ConversationMessageEntity,
  ConversationMessageTranslation,
  ConversationResponse,
  ConversationUnreadCountResponse,
  ConversationUserUnreadCountResponse,
  CreateConversationGroupDto,
  GetConversationCandidatesParams,
  GetConversationGroupsParams,
  GetConversationMessagesParams,
  GetConversationsParams,
  MuteConversationDto,
  PresignConversationAttachmentDto,
  SendConversationMessageDto,
  UpdateConversationGroupDto,
} from '../types/conversation.types';
import { ResponseType } from '../types/shared.types';
import {
  ChatActionOptionKey,
  ChatQuickReplyKey,
} from '../types/supportTicket.types';
import { api } from './api';

/**
 * Conversations.
 *
 * Two route families live under the same `/v1/conversations` prefix, and which
 * one a build talks to is decided by the API it points at rather than by
 * anything here:
 *
 *   - the ADMIN api addresses a conversation by id (`/v1/conversations/:id/...`)
 *     and adds the inbox, groups, participants and archive routes;
 *   - the CUSTOMER api has no ids at all (`/v1/conversations/messages`), because
 *     an outside user has exactly one SUPPORT thread with vGrubs. The merchant
 *     groups they are in are the exception, under `/v2/conversations/:id` -
 *     the same paths for the merchant, tablet and agent apps.
 *
 * Calling an admin route from a merchant build is a 404, not a permission
 * error, so the split is enforced by which app imports what - see
 * `helpers/appType`.
 */

export const CONVERSATIONS_PAGE_SIZE = 30;

/**
 * Cache key for the merchant's single thread.
 *
 * An outside user has exactly one conversation with vGrubs and no way to name
 * it - the routes carry no id, and the real id is unknown until the first
 * message arrives (an empty thread has none, it is created on the first send).
 * So the merchant app keys its cache on this sentinel and the socket maps
 * every payload onto it, rather than on the id the payload happens to carry.
 */
export const USER_CONVERSATION_ID = 'me';

/* -------------------------------------------------------------------------- */
/* Inbox (admin)                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Support threads plus the caller's own groups and direct messages, in one
 * list.
 *
 * Keyset paginated: for the next page pass `cursorSortAt` = the LAST row's
 * `lastMessageAt` and `cursorId` = its `id`. There is no offset paging, and
 * mixing the two would silently skip rows as the list reorders.
 */
export const getConversations = ({
  types,
  ...params
}: GetConversationsParams) =>
  api.get<ResponseType<ConversationListItemResponse[]>>('/v1/conversations', {
    params: {
      ...params,
      /* Joined here rather than handed to axios as an array: its default
         serialiser writes `types[]=support&types[]=group`, and the brackets are
         not what the endpoint reads. The spec takes either a repeated param or
         this comma separated form. */
      ...(types?.length ? { types: types.join(',') } : {}),
    },
  });

/** Full detail with the member list. */
export const getConversation = (conversationId: number | string) =>
  api.get<ResponseType<ConversationResponse>>(
    `/v1/conversations/${conversationId}`
  );

export const archiveConversation = (
  conversationId: number | string,
  archived: boolean
) =>
  api.put<ResponseType<null>>(`/v1/conversations/${conversationId}/archive`, {
    archived,
  });

/**
 * Silence push for this member only. Omit `mutedUntil` to unmute; a mention
 * still pushes through a mute.
 */
export const muteConversation = (
  conversationId: number | string,
  data: MuteConversationDto
) =>
  api.put<ResponseType<null>>(`/v1/conversations/${conversationId}/mute`, data);

/* -------------------------------------------------------------------------- */
/* Starting a conversation (admin)                                            */
/* -------------------------------------------------------------------------- */

/**
 * People you can start a conversation with.
 *
 * `internalOnly` narrows to admin / support / marketing, which is what a direct
 * message requires; `group` adds sales agents, an internal group's members;
 * `merchantGroup` adds merchants on top. Without any of them you get every
 * user, which is the pool for a support thread.
 */
export const getConversationCandidates = (
  params: GetConversationCandidatesParams
) =>
  api.get<ResponseType<ConversationCandidateResponse[]>>(
    '/v1/conversations/users',
    { params }
  );

/**
 * Idempotent: called twice for the same user it returns the same conversation,
 * so there is no need to check `hasChat` first. An empty conversation shows up
 * in the inbox straight away, sorted by when it was created.
 */
export const openSupportConversation = (userId: number | string) =>
  api.post<ResponseType<ConversationListItemResponse>>(
    `/v1/conversations/support/${userId}`
  );

/** Idempotent, same as `openSupportConversation`. Internal users only. */
export const openDirectConversation = (userId: number | string) =>
  api.post<ResponseType<ConversationListItemResponse>>(
    `/v1/conversations/direct/${userId}`
  );

/* -------------------------------------------------------------------------- */
/* Viktor (admin and support roles)                                           */
/* -------------------------------------------------------------------------- */

/**
 * The caller's own thread with Viktor. Idempotent: the first call creates it,
 * every later one returns the same row - so this is how the Viktor tab learns
 * its conversation id even while the thread is empty (an empty thread is left
 * out of `GET /v1/conversations`, whatever `types` says). 403 for a role that
 * has no Viktor - only `admin` and `support` do, permissions notwithstanding.
 */
export const openViktorConversation = () =>
  api.post<ResponseType<ConversationListItemResponse>>(
    '/v1/conversations/viktor'
  );

/**
 * Stop the answer Viktor is writing. Acknowledged on the socket, not here:
 * `viktor_run_finished` with `status: 'cancelled'` follows within ~2s, and a
 * cancelled answer is never stored. Nothing to do when Viktor is idle.
 */
export const cancelViktorRun = (conversationId: number | string) =>
  api.post<ResponseType<null>>(
    `/v1/conversations/${conversationId}/viktor/cancel`
  );

/* -------------------------------------------------------------------------- */
/* Dispatcher threads (docs/dispatcher-chat-front.md)                         */
/* -------------------------------------------------------------------------- */

/**
 * Stops the customer from writing and blocks their IP for 24 h. The customer
 * gets `canWrite: false`; the answer is the updated inbox row.
 */
export const blockConversation = (conversationId: number | string) =>
  api.post<ResponseType<ConversationListItemResponse>>(
    `/v1/conversations/${conversationId}/block`
  );

/* -------------------------------------------------------------------------- */
/* Groups (admin role)                                                        */
/* -------------------------------------------------------------------------- */

/** The creator is added automatically and becomes the owner. */
export const createConversationGroup = (data: CreateConversationGroupDto) =>
  api.post<ResponseType<ConversationResponse>>(
    '/v1/conversations/groups',
    data
  );

/**
 * Group avatars are referenced by storage key, not by url: presign, PUT the
 * file to Spaces, then send the returned `key` back as `imageKey`.
 */
export const presignConversationGroupImage = (
  data: PresignConversationAttachmentDto
) =>
  api.post<ResponseType<ConversationAttachmentPresignResponse>>(
    '/v1/conversations/groups/image/presign',
    data
  );

/** Rename, or set / clear the image. Every change writes a system message. */
export const updateConversationGroup = (
  conversationId: number | string,
  data: UpdateConversationGroupDto
) =>
  api.patch<ResponseType<ConversationResponse>>(
    `/v1/conversations/${conversationId}`,
    data
  );

export const addConversationParticipants = (
  conversationId: number | string,
  data: AddConversationParticipantsDto
) =>
  api.post<ResponseType<ConversationResponse>>(
    `/v1/conversations/${conversationId}/participants`,
    data
  );

export const removeConversationParticipant = (
  conversationId: number | string,
  userId: number | string
) =>
  api.delete<ResponseType<null>>(
    `/v1/conversations/${conversationId}/participants/${userId}`
  );

/** Available to every member, not just the owner. */
export const leaveConversation = (conversationId: number | string) =>
  api.post<ResponseType<null>>(`/v1/conversations/${conversationId}/leave`);

/* -------------------------------------------------------------------------- */
/* Unread badge                                                               */
/* -------------------------------------------------------------------------- */

/** Admin: threads and messages. */
export const getConversationsUnreadCount = () =>
  api.get<ResponseType<ConversationUnreadCountResponse>>(
    '/v1/conversations/unread-count'
  );

/**
 * Merchant variant of the same path. A different response shape per app, kept
 * as two typed functions so neither caller has to narrow a union. The v2 path
 * answers the same shape, so there is no second function for it.
 */
export const getUserConversationUnreadCount = () =>
  api.get<ResponseType<ConversationUserUnreadCountResponse>>(
    '/v1/conversations/unread-count'
  );

/** Admin only. */
export const markAllConversationsRead = () =>
  api.post<ResponseType<ConversationUnreadCountResponse>>(
    '/v1/conversations/read-all'
  );

/** Returns the caller's fresh counts - use them rather than recomputing. */
export const markConversationRead = (conversationId: number | string) =>
  api.post<ResponseType<ConversationUnreadCountResponse>>(
    `/v1/conversations/${conversationId}/read`
  );

/** Merchant variant - no id, and it returns the Support / Groups counts. */
export const markUserConversationRead = () =>
  api.post<ResponseType<ConversationUserUnreadCountResponse>>(
    '/v1/conversations/read'
  );

/* -------------------------------------------------------------------------- */
/* Messages (admin)                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Returns messages NEWEST FIRST. To page backwards through history pass
 * `cursorId` = the id of the oldest message already loaded.
 */
export const getConversationMessages = (
  conversationId: number | string,
  params: GetConversationMessagesParams
) =>
  api.get<ResponseType<ConversationMessageEntity[]>>(
    `/v1/conversations/${conversationId}/messages`,
    { params }
  );

/** Full-text search inside one conversation. `query` needs 2+ characters. */
export const searchConversationMessages = (
  conversationId: number | string,
  query: string,
  limit = CONVERSATIONS_PAGE_SIZE
) =>
  api.get<ResponseType<ConversationMessageEntity[]>>(
    `/v1/conversations/${conversationId}/messages/search`,
    { params: { query, limit } }
  );

/** Requires a `body` or at least one attachment. */
export const sendConversationMessage = (
  conversationId: number | string,
  data: SendConversationMessageDto
) =>
  api.post<ResponseType<ConversationMessageEntity>>(
    `/v1/conversations/${conversationId}/messages`,
    data
  );

/** Own messages only - the API rejects anyone else's. */
export const updateConversationMessage = (
  conversationId: number | string,
  messageId: number,
  body: string
) =>
  api.patch<ResponseType<ConversationMessageEntity>>(
    `/v1/conversations/${conversationId}/messages/${messageId}`,
    { body }
  );

/** Translates one message - see `ConversationMessageTranslation`. No body. */
export const translateConversationMessage = (
  conversationId: number | string,
  messageId: number
) =>
  api.post<ResponseType<ConversationMessageTranslation>>(
    `/v1/conversations/${conversationId}/messages/${messageId}/translate`
  );

/** Soft delete - the message stays in the list with `deletedAt` set. */
export const deleteConversationMessage = (
  conversationId: number | string,
  messageId: number
) =>
  api.delete<ResponseType<ConversationMessageEntity>>(
    `/v1/conversations/${conversationId}/messages/${messageId}`
  );

/**
 * Step 1 of 3. The file is then PUT straight to Spaces with the returned
 * `headers` (see `chatUpload` - deliberately not through this axios instance),
 * and the resulting `attachmentId` is sent with the message. Presigned files
 * that are never attached are cleaned up after 24h.
 */
export const presignConversationAttachment = (
  conversationId: number | string,
  data: PresignConversationAttachmentDto
) =>
  api.post<ResponseType<ConversationAttachmentPresignResponse>>(
    `/v1/conversations/${conversationId}/attachments/presign`,
    data
  );

/* -------------------------------------------------------------------------- */
/* Messages (merchant / tablet / agent) - no id in the path                   */
/* -------------------------------------------------------------------------- */

export const getUserConversationMessages = (
  params: GetConversationMessagesParams
) =>
  api.get<ResponseType<ConversationMessageEntity[]>>(
    '/v1/conversations/messages',
    { params }
  );

/** The thread is created on the first send, so an empty one is `[]`, not 404. */
export const sendUserConversationMessage = (data: SendConversationMessageDto) =>
  api.post<ResponseType<ConversationMessageEntity>>(
    '/v1/conversations/messages',
    data
  );

export const updateUserConversationMessage = (
  messageId: number,
  body: string
) =>
  api.patch<ResponseType<ConversationMessageEntity>>(
    `/v1/conversations/messages/${messageId}`,
    { body }
  );

export const translateUserConversationMessage = (messageId: number) =>
  api.post<ResponseType<ConversationMessageTranslation>>(
    `/v1/conversations/messages/${messageId}/translate`
  );

export const deleteUserConversationMessage = (messageId: number) =>
  api.delete<ResponseType<ConversationMessageEntity>>(
    `/v1/conversations/messages/${messageId}`
  );

export const presignUserConversationAttachment = (
  data: PresignConversationAttachmentDto
) =>
  api.post<ResponseType<ConversationAttachmentPresignResponse>>(
    '/v1/conversations/attachments/presign',
    data
  );

/* -------------------------------------------------------------------------- */
/* Chat triage actions (merchant / tablet) - roles partner, manager, staff     */
/* -------------------------------------------------------------------------- */

/**
 * Confirm what the bot proposed. No body; the answer carries no state.
 *
 * It resolves only once the action has actually run, which can take a few
 * seconds of talking to the providers - and it answers 200 even when the
 * request had already expired, in which case nothing happens. So the card's
 * state is read from the socket (`message_updated`), never from this response.
 */
export const confirmChatAction = (
  actionId: number,
  /* Which way the merchant said yes, for a card that offers more than one.
     Left off for the rest, and an omitted option on a card that has them is
     still accepted - it means the older of the two behaviours. */
  option?: ChatActionOptionKey | null
) =>
  api.post<ResponseType<null>>(
    `/v1/conversations/actions/${actionId}/confirm`,
    option ? { option } : undefined
  );

/** Decline it. Same rules as `confirmChatAction`. */
export const rejectChatAction = (actionId: number) =>
  api.post<ResponseType<null>>(`/v1/conversations/actions/${actionId}/reject`);

/**
 * Say which order a ticket is about. Only an order from that card's own
 * `orderCandidates` is accepted; anything else is a 400.
 */
export const attachChatTicketOrder = (ticketId: number, orderId: number) =>
  api.post<ResponseType<null>>(
    `/v1/conversations/actions/tickets/${ticketId}/order/${orderId}`
  );

/**
 * Tap Yes or No under the bot's question. Only while its
 * `quickReplies.active` is true; after that it is a 400.
 *
 * Not a card action in the sense above: this one writes the merchant's answer
 * into the thread as their own message (a reply to the question) and returns
 * it, so no `sendMessage` goes with it. The same message also arrives as
 * `new_message`, and the two meet on `id` in the cache.
 */
export const answerChatQuestion = (
  messageId: number,
  answer: ChatQuickReplyKey
) =>
  api.post<ResponseType<ConversationMessageEntity>>(
    `/v1/conversations/actions/questions/${messageId}/answer`,
    { answer }
  );

/* -------------------------------------------------------------------------- */
/* Merchant groups (merchant / tablet / agent) - v2, by id                    */
/* -------------------------------------------------------------------------- */

/*
 * A member's view of the merchant groups they are in. Everything about a group
 * that is not here - creating it, naming it, who is in it - is the vGrubs
 * team's, on the admin API. A member may only read, write, mute and leave.
 *
 * An id that is not a group is a 404, and one the caller is no longer in (or
 * never was) a 403 - which is how a removed member's open group finds out.
 */

/**
 * The Groups tab, newest activity first. Keyset paged like the admin inbox:
 * the next page is `cursorSortAt` = the last row's `lastMessageAt` and
 * `cursorId` = its `id`.
 */
export const getUserConversationGroups = (
  params: GetConversationGroupsParams
) =>
  api.get<ResponseType<ConversationGroupListItemResponse[]>>(
    '/v2/conversations/groups',
    { params }
  );

/** The group and its members - for mentions, the header and "Seen". */
export const getUserConversation = (conversationId: number | string) =>
  api.get<ResponseType<ConversationResponse>>(
    `/v2/conversations/${conversationId}`
  );

/** Newest first, paged by `cursorId` exactly like the admin route. */
export const getUserGroupMessages = (
  conversationId: number | string,
  params: GetConversationMessagesParams
) =>
  api.get<ResponseType<ConversationMessageEntity[]>>(
    `/v2/conversations/${conversationId}/messages`,
    { params }
  );

/** Same body as the support thread. Mentions of non-members are dropped. */
export const sendUserGroupMessage = (
  conversationId: number | string,
  data: SendConversationMessageDto
) =>
  api.post<ResponseType<ConversationMessageEntity>>(
    `/v2/conversations/${conversationId}/messages`,
    data
  );

export const updateUserGroupMessage = (
  conversationId: number | string,
  messageId: number,
  body: string
) =>
  api.patch<ResponseType<ConversationMessageEntity>>(
    `/v2/conversations/${conversationId}/messages/${messageId}`,
    { body }
  );

export const translateUserGroupMessage = (
  conversationId: number | string,
  messageId: number
) =>
  api.post<ResponseType<ConversationMessageTranslation>>(
    `/v2/conversations/${conversationId}/messages/${messageId}/translate`
  );

export const deleteUserGroupMessage = (
  conversationId: number | string,
  messageId: number
) =>
  api.delete<ResponseType<ConversationMessageEntity>>(
    `/v2/conversations/${conversationId}/messages/${messageId}`
  );

export const presignUserGroupAttachment = (
  conversationId: number | string,
  data: PresignConversationAttachmentDto
) =>
  api.post<ResponseType<ConversationAttachmentPresignResponse>>(
    `/v2/conversations/${conversationId}/attachments/presign`,
    data
  );

/** Answers the same Support / Groups counts as the v1 unread route. */
export const markUserGroupRead = (conversationId: number | string) =>
  api.post<ResponseType<ConversationUserUnreadCountResponse>>(
    `/v2/conversations/${conversationId}/read`
  );

/** Null unmutes; a far-future date mutes for good. A mention still pushes. */
export const muteUserGroup = (
  conversationId: number | string,
  data: MuteConversationDto
) =>
  api.put<ResponseType<null>>(`/v2/conversations/${conversationId}/mute`, data);

export const leaveUserGroup = (conversationId: number | string) =>
  api.post<ResponseType<null>>(`/v2/conversations/${conversationId}/leave`);
